import React, { useEffect, useState } from 'react';
import { AssignSalesPersonModal } from '../../../UnitTypeModule/containers/unit-types-list/components/assign-salesPerson-modal/assign-salesPerson-modal.component';
import Button, { ButtonSize } from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { UserRole } from '../../../shared/enums/user-role.enum';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { UnitTypeUiModel } from '../../../shared/ui-models/unit-type.ui-model';
import { UserUIModel } from '../../../shared/ui-models/user.ui-model';
import { createSalesPerson, getSalesPersons } from '../../data.service';

export const SalesPersonsContainer: React.FC = () => {
  const [salesPersons, setSalesPersons] = useState<UserUIModel[]>([]);
  const [showAssignSalesPersonModal, setShowAssignSalesPersonModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => loadSalesPersons(), []);

  const showSuccessNotification = () => {
    birdToast.success('The selected user was saved as a sales person successfully.');
  };

  const showErrorNotification = () => {
    birdToast.error('Please try again or contact support if the problem continues.');
  };

  const loadSalesPersons = () => {
    setIsLoading(true);
    getSalesPersons()
      .then((response: UserUIModel[] | ErrorInfo) => {
        if (!isErrorInfo(response)) {
          setSalesPersons(response);
        } else {
          setSalesPersons([]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChooseSalesPerson = (salesPerson: UnitTypeUiModel) => {
    if (!salesPerson.partnerId) {
      showErrorNotification();
      return;
    }

    setIsSaving(true);
    createSalesPerson({
      id: salesPerson.partnerId,
      name: salesPerson.partnerUsername,
      email: salesPerson.partnerEmail,
      phone: salesPerson.partnerPhone,
      role: UserRole.SALES_PERSON
    })
      .then((response: UserUIModel | ErrorInfo) => {
        if (!isErrorInfo(response)) {
          setShowAssignSalesPersonModal(false);
          showSuccessNotification();
          loadSalesPersons();
        } else {
          showErrorNotification();
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <div className="p-8">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="m-0 text-4xl font-gilroy-bold text-neutral-500">Sales Persons</h1>
          <p className="mt-3 max-w-4xl text-2xl font-gilroy-medium text-gray-600">
            Manage users who can be selected as sales persons in reservations.
          </p>
        </div>
        <Button
          disabled={isSaving}
          onClick={() => {
            setShowAssignSalesPersonModal(true);
          }}
          tracking={{
            action: 'add',
            category: 'sales-persons',
            label: 'open-assign-sales-person-modal'
          }}
          size={ButtonSize.medium}
        >
          Add User
        </Button>
      </div>

      <section className="min-h-[calc(100vh-190px)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-end border-b border-gray-200 px-6 py-5">
          <span className="text-lg font-gilroy-bold text-neutral-500">
            {isLoading
              ? 'Loading sales persons...'
              : `${salesPersons.length} user${salesPersons.length === 1 ? '' : 's'} available`}
          </span>
        </div>

        <div className="overflow-x-auto pb-40">
          <table
            className="w-full min-w-[1050px] table-fixed border-separate border-spacing-0 text-left"
            aria-label="Sales persons table"
          >
            <colgroup>
              <col className="w-[34%]" />
              <col className="w-[42%]" />
              <col className="w-[24%]" />
            </colgroup>
            <thead>
              <tr className="border-b border-gray-200 text-xl font-gilroy-bold text-neutral-700">
                <th scope="col" className="border-b border-gray-200 px-10 py-7 font-semibold">
                  Name
                </th>
                <th scope="col" className="border-b border-gray-200 px-6 py-7 font-semibold">
                  Email
                </th>
                <th scope="col" className="border-b border-gray-200 px-6 py-7 font-semibold">
                  Phone
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    className="px-10 py-12 text-center text-xl font-gilroy-medium text-neutral-400"
                    colSpan={3}
                  >
                    Loading sales persons...
                  </td>
                </tr>
              ) : null}

              {!isLoading && salesPersons.length === 0 ? (
                <tr>
                  <td
                    className="px-10 py-12 text-center text-xl font-gilroy-medium text-neutral-400"
                    colSpan={3}
                  >
                    No sales persons found. Click Add User to select or create one.
                  </td>
                </tr>
              ) : null}

              {!isLoading &&
                salesPersons.map((salesPerson) => (
                  <tr
                    key={salesPerson.id || salesPerson.phone || salesPerson.email}
                    className="text-2xl font-gilroy-medium text-neutral-700"
                  >
                    <th
                      scope="row"
                      className="whitespace-nowrap px-10 py-9 text-left font-gilroy-medium align-top"
                    >
                      {salesPerson.name || '-'}
                    </th>
                    <td className="truncate px-6 py-9 align-top" title={salesPerson.email || '-'}>
                      {salesPerson.email || '-'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-9 align-top">
                      {salesPerson.phone || '-'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      {showAssignSalesPersonModal && (
        <AssignSalesPersonModal
          config={{ showModal: showAssignSalesPersonModal }}
          eventHandlers={{
            closeHandler: () => {
              setShowAssignSalesPersonModal(false);
            },
            chooseUser: handleChooseSalesPerson
          }}
        />
      )}
    </div>
  );
};

export default SalesPersonsContainer;
